import type {
  LeaguePublic,
  DivisionPublic,
  Schemas,
} from "@softmax/observatory-client/types";

import {
  resolveGameLeague,
  type GamePageContext,
} from "@/features/game/data/index";

import { playLeagueOf } from "../lib/league";
import type { PublicPlayShell } from "../lib/types";
import type { PlayApi } from "./play-api";
export function createPublicPlayShellReader({
  signal,
  context,
  api,
}: {
  signal?: AbortSignal | undefined;
  context: GamePageContext | null | undefined;
  api: PlayApi;
}) {
  async function featuredDivision(league: LeaguePublic) {
    const options = { anonymous: league.public };
    const [featured, divisions] = await Promise.all([
      api.get<Schemas["LeagueFeaturedMatchPublic"] | null>({
        path: `/v2/leagues/${encodeURIComponent(league.id)}/featured-match?mode=latest`,
        ...options,
      }),
      api.get<DivisionPublic[]>({
        path: `/v2/divisions?league_id=${encodeURIComponent(league.id)}&type=competition`,
        ...options,
      }),
    ]);
    const division = featured?.round.division ?? divisions[0];
    return {
      divisionId: division?.id ?? null,
      divisionName: division?.name ?? null,
    };
  }
  const publicShell = async (slug: string): Promise<PublicPlayShell | null> => {
    const resolution = await resolveGameLeague({
      signal,
      publicAddress: slug,
      context: context ?? null,
    });

    if (resolution.status === "not-found") return null;

    if (resolution.status !== "ok")
      throw new Error("Play league is unavailable");

    return {
      league: playLeagueOf({
        league: resolution.league,
        divisionId: null,
        divisionName: "League",
      }),
      divisionId: null,
    };
  };
  const featuredPublicShell = async (
    slug: string,
  ): Promise<PublicPlayShell | null> => {
    const resolution = await resolveGameLeague({
      signal,
      publicAddress: slug,
      context: context ?? null,
    });

    if (resolution.status === "not-found") return null;

    if (resolution.status !== "ok")
      throw new Error("Play league is unavailable");

    const featured = await featuredDivision(resolution.league);
    return {
      league: playLeagueOf({
        league: resolution.league,
        divisionId: featured.divisionId,
        divisionName: featured.divisionName ?? "League",
      }),
      divisionId: featured.divisionId,
    };
  };
  return { featuredDivision, publicShell, featuredPublicShell };
}
